import React, { useState, useEffect, useRef } from "react";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { Dialog } from "primereact/dialog";
import { MapperEditorMode, MapperOperationObject } from "utils/enums";
import { useUserState } from "store/user-store";
import { I, T } from "services/lang/LanguageService";
import { DataTable } from "primereact/datatable";
import { easasConfirmDialog } from "components/fragments/easas-global-fragment/EasasGlobalConfirmDialog";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import {
  useCreateDatasetMapperData,
  useDeleteDatasetMapperData,
  useGetDatasetMappersData,
  useUpdateDatasetMapperData,
} from "api/api-hooks/dataset-mapper-hooks";
import { PermissionGuard } from "components/wrappers/PermissionGuard";
import MapperMappings from "./MapperMappings";
import CreateEditDatasetMapper from "./CreateEditDatasetMapper";
import { DatasetStringValueMapperDTO } from "../../../charts/interfaces";
import { downloadBlob } from "components/dashboard/product-template-table/utils/utils";

const ManageDatasetMappers = () => {
  const [datasetMappersDialogVisibility, setDatasetMappersDialogVisibility] = useState<boolean>(false);
  const [editorMode, setEditorMode] = useState<MapperEditorMode | null>();
  const [mapperMappingDialogVisibility, setMapperMappingDialogVisibility] = useState<boolean>(false);
  const [selectedMapper, setSelectedMapper] = useState<DatasetStringValueMapperDTO>();
  const [mappers, setMappers] = useState<DatasetStringValueMapperDTO[]>([]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const currentCompany = useUserState.getState().companyContext?.title;

  const { data: currentMappers, isLoading, isError } = useGetDatasetMappersData();

  const createMutation = useCreateDatasetMapperData();
  const updateMutation = useUpdateDatasetMapperData();
  const deleteMutation = useDeleteDatasetMapperData();

  useEffect(() => {
    if (!isLoading && !isError) {
      setMappers(currentMappers?.length ? [...currentMappers] : []);
    }
  }, [currentMappers, isLoading, isError]);

  const handleDeleteDialogConfirm = (mapper: DatasetStringValueMapperDTO) => {
    const newMappersArray = mappers.filter(m => m.id !== mapper.id);
    setMappers(newMappersArray);
    deleteMutation.mutate(mapper);
  };

  const handlerowDeleteClick = (mapper: DatasetStringValueMapperDTO) => {
    setSelectedMapper(mapper);
    easasConfirmDialog({
      icon: "pi pi-exclamation-triangle",
      header: T("confirm-dialog.confirmation"),
      message: T("confirm-dialog.are-you-sure-you-want-to-delete-this-mappings-group"),
      confirmedCallback: () => handleDeleteDialogConfirm(mapper),
    });
  };

  const handleRowEditClick = (mapper: DatasetStringValueMapperDTO) => {
    setSelectedMapper(mapper);
    handleOpenEditDialog();
  };

  const handleEditDialogSave = (updatedMapper: DatasetStringValueMapperDTO) => {
    if (updatedMapper) {
      for (let i = 0; i < mappers.length; i++) {
        if (mappers[i].id === updatedMapper.id) {
          mappers[i] = updatedMapper;
          updateMutation.mutate(updatedMapper);
          break;
        }
      }
    }
    handleCloseEditDialog();
  };

  const createDatasetMapper = (newMapper: DatasetStringValueMapperDTO) => {
    if (newMapper) {
      setMappers([...mappers, newMapper]);
      createMutation.mutate(newMapper);
    }
    handleCloseEditDialog();
  };

  const handleOpenCreateDialog = () => {
    setEditorMode(MapperEditorMode.CREATE);
    setDatasetMappersDialogVisibility(true);
  };

  const handleOpenEditDialog = () => {
    setEditorMode(MapperEditorMode.EDIT);
    setDatasetMappersDialogVisibility(true);
  };

  const handleCloseEditDialog = () => {
    setEditorMode(null);
    setDatasetMappersDialogVisibility(false);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const parsed = JSON.parse(text);

      if (typeof parsed.title !== "string" || typeof parsed.mappings !== "object" || typeof parsed.mappingIcons !== "object") {
        throw new Error(T("alert.json-is-not-valid"));
      }
      const currentIds = mappers.map(m => Number(m.id));
      const newMapperId = currentIds.length ? Math.max(...currentIds) + 1 : 0;

      const newMapper: DatasetStringValueMapperDTO = {
        id: newMapperId,
        title: parsed.title,
        company: currentCompany,
        global: false,
        mappings: parsed.mappings,
        mappingIcons: parsed.mappingIcons,
      };
      setMappers(prev => [...prev, newMapper]);
      createMutation.mutate(newMapper);
    } catch (error: unknown) {
      easasAlert({
        summary: T("alert.json-is-not-valid"),
        detail: (error as Error).message,
        severity: "error",
      });
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const mappingsDisplayTemplate = (rowData: DatasetStringValueMapperDTO) => {
    const handleMappingClick = () => {
      setSelectedMapper(rowData);
      setMapperMappingDialogVisibility(true);
    };
    return (
      <div>
        <div>
          {Object.keys(rowData.mappings).length === 0 ? (
            <h3>{T("word.no-mappings")}</h3>
          ) : (
            <ul>
              {Object.entries(rowData.mappings)
                .slice(0, 3)
                .map(([key, value]) => (
                  <li key={key}>
                    {key}: {typeof value === "string" ? I(value) : value}
                  </li>
                ))}
            </ul>
          )}
        </div>
        {Object.keys(rowData.mappings).length > 3 && (
          <Button
            type="button"
            label={T("action.see-all-mappings")}
            className="p-button-text p-0"
            onClick={handleMappingClick}
          />
        )}
      </div>
    );
  };

  const mapperEditButtonTemplate = (mapperData: DatasetStringValueMapperDTO) =>
    mapperData.global ? (
      <PermissionGuard permissions="ADMIN">
        <Button
          type="button"
          icon="pi pi-pencil"
          tooltip={T("action.edit-mappings")}
          className="p-button-rounded p-button-text p-0"
          onClick={() => handleRowEditClick(mapperData)}
        />
      </PermissionGuard>
    ) : (
      <Button
        type="button"
        icon="pi pi-pencil"
        tooltip={T("action.edit-mappings")}
        className="p-button-rounded p-button-text p-0"
        onClick={() => handleRowEditClick(mapperData)}
      />
    );

  const mapperRemoveButtonTemplate = (mapperData: DatasetStringValueMapperDTO) =>
    mapperData.global ? (
      <PermissionGuard permissions="ADMIN">
        <Button
          type="button"
          icon="pi pi-trash"
          tooltip={T("action.remove-mapping-group")}
          className="const-del-btn p-button-rounded p-button-text p-button-danger p-0"
          onClick={() => handlerowDeleteClick(mapperData)}
        />
      </PermissionGuard>
    ) : (
      <Button
        type="button"
        icon="pi pi-trash"
        tooltip={T("action.remove-mapping-group")}
        className="const-del-btn p-button-rounded p-button-text p-button-danger p-0"
        onClick={() => handlerowDeleteClick(mapperData)}
      />
    );

  const mapperDownloadButtonTemplate = (mapperData: DatasetStringValueMapperDTO) => (
    <Button
      type="button"
      icon="pi pi-download"
      tooltip={T("action.download-mapper-json")}
      className="p-button-rounded p-button-text p-0"
      onClick={() => handleDownloadClick(mapperData, MapperOperationObject.MAPPER)}
    />
  );

  const handleDownloadClick = (mapperData: DatasetStringValueMapperDTO, operationObject: MapperOperationObject) => {
    const isMappings = operationObject === MapperOperationObject.MAPPINGS;
    const dataToDownload = isMappings ? mapperData.mappings : mapperData;
    const blob = new Blob([JSON.stringify(dataToDownload, null, 2)], {
      type: "application/json",
    });
    const fileName = `${mapperData.title}${isMappings ? "-mappings" : ""}.json`;
    downloadBlob(blob, fileName, "application/json");
  };

  return (
    <div>
      {isLoading && <div>{T("word.loading")}</div>}
      {isError && <div>{T("error.error-fetching-data")}</div>}
      {!isLoading && !isError && (
        <div>
          <DataTable
            value={[...mappers].sort((a, b) => (a.id ?? 0) - (b.id ?? 0))}
            editMode="row"
            dataKey="id"
            scrollHeight="300px"
            responsiveLayout="scroll"
            size="small"
          >
            <Column field="title" header={T("word.title")} style={{ width: "25%" }} />
            <Column
              header={T("word.global")}
              body={rowData => (rowData.global ? "Global" : "Not Global")}
              style={{ width: "12%" }}
            />
            <Column
              header={T("word.mappings")}
              body={(rowData: DatasetStringValueMapperDTO) => mappingsDisplayTemplate(rowData)}
              style={{ width: "60%" }}
            />
            <Column body={mapperData => mapperRemoveButtonTemplate(mapperData)} style={{ width: "1%" }} />
            <Column body={mapperData => mapperEditButtonTemplate(mapperData)} style={{ width: "1%" }} />
            <Column body={mapperData => mapperDownloadButtonTemplate(mapperData)} style={{ width: "1%" }} />
          </DataTable>
        </div>
      )}

      <input type="file" accept="application/json" ref={fileInputRef} style={{ display: "none" }} onChange={handleFileUpload} />

      <Dialog
        style={{ width: "min(100vw, 800px)", maxHeight: "400px" }}
        header={T("word.mappings")}
        visible={mapperMappingDialogVisibility}
        onHide={() => setMapperMappingDialogVisibility(false)}
        resizable
      >
        {selectedMapper && <MapperMappings selectedMapper={selectedMapper} />}
      </Dialog>

      {datasetMappersDialogVisibility && editorMode === MapperEditorMode.CREATE && (
        <CreateEditDatasetMapper
          mode={MapperEditorMode.CREATE}
          isVisible={datasetMappersDialogVisibility}
          setIsVisible={setDatasetMappersDialogVisibility}
          handleDownloadClick={handleDownloadClick}
          onSave={createDatasetMapper}
        />
      )}

      {selectedMapper && datasetMappersDialogVisibility && editorMode === MapperEditorMode.EDIT && (
        <CreateEditDatasetMapper
          mapperData={selectedMapper}
          mode={MapperEditorMode.EDIT}
          isVisible={datasetMappersDialogVisibility}
          setIsVisible={setDatasetMappersDialogVisibility}
          handleDownloadClick={handleDownloadClick}
          onSave={handleEditDialogSave}
        />
      )}

      <div className="w-12 flex justify-content-end gap-2 mt-2">
        <Button
          className="component-editor-button icon-green"
          label={`${T("action.upload-mapper")} (JSON)`}
          icon="pi pi-upload"
          onClick={() => fileInputRef.current?.click()}
        />
        <Button
          className="component-editor-button icon-green"
          label={T("action.create-new")}
          icon="pi pi-plus"
          onClick={handleOpenCreateDialog}
        />
      </div>
    </div>
  );
};

export default ManageDatasetMappers;
